import type { DraftStatus } from "@convex/tables/disputeDrafts";
import type { ActionRisk } from "@/lib/actions/types";
/**
 * DisputeWell Design System — Token Registry
 *
 * SINGLE SOURCE OF TRUTH for the design system. Every downstream artifact
 * (globals.css, tailwind.config.ts, eslint-tokens.mjs, types.d.ts,
 * microcopy-keys.ts) is generated from this file via `npm run tokens`.
 *
 * Source-of-decisions:
 * - Brand intake v2 (locked):    docs/brand/intake-answers.md (color/typography/motion/density/iconography/logo)
 * - Brand intake v3 (locked):    docs/brand/intake-answers-v3.md (composition/state/a11y/motion-choreography)
 * - Implementation blueprint:    docs/implementation-blueprint.md (§3 file shape)
 * - Design-system feature spec:  features/design-system.yml
 *
 * Component variant matrices live in `src/design-system/component-tokens.ts`.
 * Layout dimensions are exported as the `LayoutToken` type below.
 *
 * DO NOT add a token without:
 *   1. A source citation comment (intake §X.Y, gap C.N, or RFC link)
 *   2. Updating the corresponding section in microcopy.ts or component-tokens.ts if needed
 *   3. Running `npm run tokens` to regenerate downstream artifacts
 *
 * DO NOT define colors in globals.css, tailwind.config.ts, or component files.
 * If a token doesn't exist here, it doesn't exist.
 *
 * Format contract (blueprint §3.4):
 *   Every color value is `oklch(L C H)` with the hex equivalent in a comment.
 *   generate.ts (Step 6) validates round-trip: every comment hex → OKLCH within ΔE < 1.
 *   Until Step 6 lands, OKLCH approximations are best-effort; see commit history
 *   for the round-trip verification step that closes Phase 1 Step 2.
 */

// ---------------------------------------------------------------------------
// ─── Adaptive color primitive ───
// ---------------------------------------------------------------------------
/** A color that adapts to light/dark mode. */
export interface AdaptiveColor {
    light: string;
    dark: string;
}

/** A color that is mode-agnostic (light = dark = same hex). Used only for
 * Fluid gradient tokens per intake §2.1 + v2 §C.6 LOCKED. */
export interface FixedColor {
    value: string;
}

// ---------------------------------------------------------------------------
// ─── The palette — every colour literal in this file, written once ───
// ---------------------------------------------------------------------------

/**
 * The raw colours the roles below read. A name says where a colour sits — a hue
 * word (neutral · cream · sand · stone · slate · blue · green · …) and a lightness
 * step (OKLCH L × 100) — never what it is. Emitted as --palette-{name}, and every
 * colour role emits as var(--palette-{name}) (generate.ts maps a role's value back
 * to its palette name), so retuning one literal here retunes every role that reads
 * it. TS consumers keep reading literal oklch strings through these references.
 */
export const palette = {
    "amber-30": "oklch(0.30 0.058 80)",
    "amber-46": "oklch(0.46 0.105 60)",
    "amber-74": "oklch(0.74 0.111 80)",
    "amber-95": "oklch(0.95 0.057 95)",
    "blue-32": "oklch(0.32 0.040 250)",
    "blue-36": "oklch(0.36 0.123 250)",
    "blue-62": "oklch(0.62 0.110 250)",
    "blue-68": "oklch(0.68 0.034 250)",
    "cream-91": "oklch(0.91 0.005 90)",
    "cream-94": "oklch(0.94 0.005 90)",
    "cyan-81": "oklch(0.81 0.083 220)",
    "emerald-51": "oklch(0.51 0.140 145)",
    "emerald-79": "oklch(0.79 0.176 145)",
    "green-36": "oklch(0.36 0.058 168)",
    "green-66": "oklch(0.66 0.114 168)",
    "neutral-18": "oklch(0.18 0 0)",
    "neutral-20": "oklch(0.20 0 0)",
    "neutral-21": "oklch(0.21 0 0)",
    "neutral-23": "oklch(0.23 0 0)",
    "neutral-24": "oklch(0.24 0 0)",
    "neutral-27": "oklch(0.27 0 0)",
    "neutral-96": "oklch(0.96 0 0)",
    "neutral-97": "oklch(0.97 0 0)",
    "neutral-98": "oklch(0.98 0 0)",
    "neutral-99": "oklch(0.99 0 0)",
    "neutral-100": "oklch(1.00 0 0)",
    "orange-40": "oklch(0.40 0.145 35)",
    "orange-68": "oklch(0.68 0.18 45)",
    "orange-74": "oklch(0.74 0.156 50)",
    "pink-40": "oklch(0.40 0.155 0)",
    "pink-74": "oklch(0.74 0.153 0)",
    "purple-45": "oklch(0.45 0.205 300)",
    "purple-65": "oklch(0.65 0.142 295)",
    "red-36": "oklch(0.36 0.117 25)",
    "red-45": "oklch(0.45 0.182 27)",
    "red-62": "oklch(0.62 0.200 25)",
    "red-63": "oklch(0.63 0.221 27)",
    "slate-8": "oklch(0.08 0.01 254)",
    "slate-32": "oklch(0.32 0.005 264)",
    "slate-45": "oklch(0.45 0.014 264)",
    "slate-50": "oklch(0.50 0.005 264)",
    "slate-55": "oklch(0.55 0.005 264)",
    "slate-62": "oklch(0.62 0.005 264)",
    "slate-69": "oklch(0.69 0.014 264)",
    "slate-91": "oklch(0.91 0.004 264)",
    "stone-51": "oklch(0.51 0.005 35)",
    "stone-69": "oklch(0.69 0.005 60)",
    "teal-58": "oklch(0.58 0.105 175)",

    // The scoped palettes, hoisted 2026-09-20 — the badge tone ladder
    // (atoms/categories/badges.css) and the dev console's always-dark chrome
    // (dev.css) each held their own hex literals, and so did the static colours
    // (--color-*, merged 2026-10-13: black, the payment-network and LinkedIn
    // brand colours, the device mock-up, the auth glass). Same pixels, one definition
    // site, named by hue word and lightness step like every entry above. A
    // letter suffix means another value already sits on that step.
    "blue-22": "#111a2e",          // L 0.22 C 0.042 H 265
    "blue-42": "#1e40af",          // L 0.424 C 0.181 H 266 — Amex
    "blue-43": "#004f99",          // L 0.431 C 0.138 H 254
    "blue-52": "#0a66c2",          // L 0.516 C 0.163 H 255 — LinkedIn
    "blue-55": "#64748b",          // L 0.554 C 0.041 H 257
    "blue-55a": "#2563eb",         // L 0.546 C 0.215 H 263 — Visa
    "blue-68b": "#339cff",         // L 0.682 C 0.173 H 251
    "cyan-83": "#7dd3fc",          // L 0.828 C 0.101 H 230
    "gold-92": "#fde68a",          // L 0.924 C 0.115 H 96
    "green-53": "#15803d",         // L 0.527 C 0.137 H 150 — the run-fluid overlay's success glyph, the JCB tile
    "green-54": "#008635",         // L 0.541 C 0.155 H 148
    "green-62": "#00a240",         // L 0.621 C 0.18 H 148
    "green-68": "#04b84c",         // L 0.683 C 0.195 H 148
    "neutral-0": "#000000",        // L 0.000 C 0.000 H 0 — black
    "neutral-37": "#404040",       // L 0.371 C 0.000 H 90 — the device mock-up frame
    "orange-47": "#923b0f",        // L 0.466 C 0.129 H 43
    "orange-63": "#e25507",        // L 0.63 C 0.189 H 41
    "orange-70": "#fb6a22",        // L 0.696 C 0.193 H 43
    "orange-70a": "#f97316",       // L 0.705 C 0.187 H 48 — Discover
    "red-40": "#7f1d1d",           // L 0.396 C 0.133 H 26
    "red-88": "#fecaca",           // L 0.885 C 0.059 H 18
    "rose-94": "#fee2e2",          // L 0.936 C 0.031 H 18
    "slate-13": "#020617",         // L 0.129 C 0.041 H 265 — the auth page's dark glass
    "slate-18": "#0b1220",         // L 0.183 C 0.031 H 263
    "slate-21": "#0f172a",         // L 0.208 C 0.04 H 266
    "slate-45a": "#475569",        // L 0.446 C 0.037 H 257 — the card-brand fallback tile
    "slate-71": "#94a3b8",         // L 0.711 C 0.035 H 257
    "slate-87": "#cbd5e1",         // L 0.869 C 0.02 H 253
    "violet-48": "#6b3ab4",        // L 0.476 C 0.183 H 297
    "violet-75": "#be95fa",        // L 0.747 C 0.147 H 302
} as const;

// ---------------------------------------------------------------------------
// ─── Themeable colors (overridable by org presets) ───
// ---------------------------------------------------------------------------

/**
 * THEMEABLE tokens — overridden by org presets and custom themes.
 * Source: intake §2.1 (brand) · §2.2 (surfaces) · §2.4 (border/input/ring) ·
 *         §2.5 (destructive) · §2.6 (sidebar) · §2.7 (highlight/row) · §2.8 (charts)
 *
 * Every preset MUST provide values for ALL of these.
 */
export const themeableTokens = {
    // ── Brand (intake §2.1) — single-brand discipline; no second brand color
    primary: { light: palette["green-36"], /* #0F5D4C */ dark: palette["green-66"] /* #1FA68C */ },
    "primary-foreground": { light: palette["cream-94"], /* #F4F3EC */ dark: palette["neutral-18"] /* #161817 */ },
    // accent is FUNCTIONAL (subtle hover/highlight), NOT a second brand color (intake §2.1 notes)
    accent: { light: palette["neutral-96"], /* #F0F1F2 */ dark: palette["neutral-21"] /* #252830 */ },
    "accent-foreground": { light: palette["neutral-18"], /* #161817 */ dark: palette["cream-94"] /* #F4F3EC */ },

    // ── CRM surfaces (intake §2.2) — 4-tier elevation: page → muted → card → popover
    background: { light: palette["neutral-99"], /* #FBFBFC */ dark: palette["neutral-20"] /* #1B1D20 */ },
    foreground: { light: palette["neutral-20"], /* #1B1D20 */ dark: palette["cream-94"] /* #F4F3EC */ },
    card: { light: palette["neutral-100"], /* #FFFFFF */ dark: palette["neutral-23"] /* #22252A */ },
    "card-foreground": { light: palette["neutral-20"], /* #1B1D20 */ dark: palette["cream-94"] /* #F4F3EC */ },
    popover: { light: palette["neutral-100"], /* #FFFFFF */ dark: palette["neutral-27"] /* #2A2D33 */ },
    "popover-foreground": { light: palette["neutral-20"], /* #1B1D20 */ dark: palette["cream-94"] /* #F4F3EC */ },
    muted: { light: palette["neutral-97"], /* #F4F5F6 */ dark: palette["neutral-24"] /* #262930 */ },
    "muted-foreground": { light: palette["slate-45"], /* #5F6470 */ dark: palette["slate-69"] /* #9BA1AB */ },

    // ── Border / input / ring (intake §2.4)
    // L bumped from 0.91→0.62 (light) and 0.27→0.50 (dark) to clear WCAG 2.2 AA
    // 3:1 large-text on --background per v3 §O.2 contrast-lint LOCKED. Light L=0.62
    // is darker than the prior hairline because it must also clear 3:1 on the
    // warmer Portal background (oklch L=0.97); a single shared border value works
    // for both CRM (L=0.99) and Portal (L=0.97) surfaces. Visually stronger than
    // intake §2.4 "subtle hairline" preference; mandatory under §O.2.
    border: { light: palette["slate-62"], /* WCAG AA */ dark: palette["slate-50"] /* WCAG AA */ },
    // dark `input` intentionally lighter than dark `border` for affordance contrast (intake §2.4)
    input: { light: palette["slate-62"], /* WCAG AA */ dark: palette["slate-55"] /* WCAG AA */ },
    ring: { light: palette["green-36"], /* #0F5D4C */ dark: palette["green-66"] /* #1FA68C */ },

    // ── Destructive (intake §2.5) — light keeps deep red #791F1F with cream text;
    // dark brightens destructive to clear WCAG 2.2 AA 4.5:1 on dark --background,
    // which forces a DARK foreground on the bright-red button (the dark bg L=0.20
    // forces destructive into a Y window where no light foreground can clear 4.5:1
    // on the destructive bg — only a dark foreground does). Both --destructive and
    // --destructive-foreground are now adaptive. v3 §O.2 contrast lock overrides
    // the intake §2.5 "same hex both modes" preference.
    destructive: { light: palette["red-36"], /* #791F1F */ dark: palette["red-62"] /* WCAG AA */ },
    "destructive-foreground": { light: palette["cream-94"], /* #F4F3EC */ dark: palette["neutral-18"] /* WCAG AA — dark text on bright-red dark dest btn */ },

    // ── Sidebar (intake §2.6) — dark sidebar-fg is NOT off-white; eye-strain reduction
    sidebar: { light: palette["neutral-97"], /* #F4F5F6 */ dark: palette["neutral-18"] /* #161817 */ },
    "sidebar-foreground": { light: palette["neutral-20"], /* #1B1D20 */ dark: palette["cream-91"] /* #E5E5E0 */ },
    "sidebar-accent": { light: palette["slate-91"], /* #E5E7EB */ dark: palette["neutral-23"] /* #22252A */ },
    "sidebar-accent-foreground": { light: palette["neutral-20"], /* #1B1D20 */ dark: palette["cream-91"] /* #E5E5E0 */ },

    // ── Highlight + row tokens (intake §2.7) — highlight is warm amber, NOT brand-tinted
    // Reasoning (§2.7): brand green tinting would dilute the primary semantic meaning of the brand color.
    highlight: { light: palette["amber-95"], /* #FEF3C7 */ dark: palette["amber-30"] /* #3F3416 */ },
    "row-stripe": { light: palette["neutral-98"], /* #FAFAFB */ dark: palette["neutral-21"] /* #1E2126 */ },

    // ── Charts (intake §2.8) — 5-color deuteranopia-safe palette
    // Adjacency rule: dataviz-green (brand green) MUST NOT be adjacent to score-poor or destructive.
    // chart-1..5 is the indexed dataviz scale (GI-CSSVAR-MINT-01).
    "chart-1": { light: palette["green-36"], /* #0F5D4C — brand green */ dark: palette["green-66"] /* #1FA68C */ },
    "chart-2": { light: palette["blue-36"], /* #0C447C — info blue */ dark: palette["blue-62"] /* #5B8DC4 */ },
    "chart-3": { light: palette["amber-46"], /* #854F0B — warning amber */ dark: palette["amber-74"] /* #D4A356 */ },
    "chart-4": { light: palette["purple-45"], /* #7B2CBF — purple */ dark: palette["purple-65"] /* #B08AE8 */ },
    "chart-5": { light: palette["stone-51"], /* #79706E — neutral */ dark: palette["stone-69"] /* #A8A6A2 */ },

    // ── DEPRECATED back-compat aliases (intake §C.8 LOCKED — alias then deprecate over 1-2 PRs)
    // These exist ONLY to keep ~116 existing call sites compiling until Step 10-13 component rewrites.
    // DO NOT add new usages. ESLint rule (Step 7) will flag any new bg-secondary / bg-brand / etc.
    // Removal: tracked for Phase 1 close (Step 28).
    secondary: { light: palette["neutral-97"], /* #F4F5F6 — alias muted (intake §C.8) */ dark: palette["neutral-24"] /* #262930 */ },
    "secondary-foreground": { light: palette["slate-45"], /* #5F6470 — alias muted-foreground */ dark: palette["slate-69"] /* #9BA1AB */ },
    brand: { light: palette["green-36"], /* #0F5D4C — alias primary */ dark: palette["green-66"] /* #1FA68C */ },
} as const satisfies Record<string, AdaptiveColor>;

// ---------------------------------------------------------------------------
// ─── Fixed colors (universal meaning, never overridden) ───
// ---------------------------------------------------------------------------

/**
 * FIXED tokens — NEVER overridden by org presets.
 * These carry universal meaning that org-customization would break (e.g. green = success).
 * Categorized below into status / score / bureau / dispute-state / action-risk / fluid / semantic
 * per blueprint §3.2 section order.
 */

// ---------------------------------------------------------------------------
// ─── Status / state colors (CRM workflow) ───
// ---------------------------------------------------------------------------

/**
 * 6 CRM client-status tokens — all alias the chart palette per v2 §C.2.
 * Status set: active, paused, on_hold, completed, canceled, archived.
 * Source: convex/schema.ts customer status enum + v2 §C.2 LOCKED.
 *
 * ⚠️ DIVERGENCE-1/2 RATIFIED (design-system-completion-20260527 Decision 7):
 *   Intake §C.2 LOCKED: paused=chart-3 amber, on_hold=chart-2 blue.
 *   tokens.ts: paused=dataviz-purple (suspended state), on_hold=chart-3 amber.
 *   Semantic rationale: purple communicates deliberate suspension; amber communicates
 *   attention needed. Operator decision 2026-05-27: HONOR TOKENS.TS.
 */
export const statusTokens = {
    // active = dataviz-green brand green ("good/normal/working")
    "status-active": { light: palette["green-36"], /* #0F5D4C */ dark: palette["green-66"] /* #1FA68C */ },
    // completed = emerald (celebration tier — also aliased as score-excellent per v2 §G.3 obs 2)
    "status-completed": { light: palette["emerald-51"], /* #15803D */ dark: palette["emerald-79"] /* #4ADE80 */ },
    // paused = dataviz-purple (suspended state); DIVERGENCE-1: intake §C.2 specified amber; HONOR TOKENS.TS per Decision 7
    "status-paused": { light: palette["purple-45"], /* #7B2CBF */ dark: palette["purple-65"] /* #B08AE8 */ },
    // on_hold = chart-3 amber (attention needed); DIVERGENCE-2: intake §C.2 specified blue; HONOR TOKENS.TS per Decision 7
    "status-on-hold": { light: palette["amber-46"], /* #854F0B */ dark: palette["amber-74"] /* #D4A356 */ },
    // canceled = destructive aliased — tracks --destructive's dark adaptation
    // for the same WCAG AA reasoning (see --destructive comment in themeableTokens).
    "status-canceled": { light: palette["red-36"], /* #791F1F */ dark: palette["red-62"] /* WCAG AA — matches --destructive dark */ },
    // archived = dataviz-neutral (historical / out of view)
    "status-archived": { light: palette["stone-51"], /* #79706E */ dark: palette["stone-69"] /* #A8A6A2 */ },
} as const satisfies Record<string, AdaptiveColor>;

// ---------------------------------------------------------------------------
// ─── Score gauge colors (credit-repair domain) ───
// ---------------------------------------------------------------------------

/**
 * 5 score-tier tokens for credit score 300-850 ranges.
 * Source: v2 §C.1 LOCKED. NOTE: score-poor is a DIFFERENT red from destructive (per v2 §G.3 obs 3):
 *   destructive (#791F1F) = "this action is dangerous/irreversible"
 *   score-poor (#B91C1C/#EF4444) = "this state is bad but not actionable from here"
 * The two reds appear together (score-poor badge next to a Delete button) — intended.
 *
 * Adjacency rule: score-poor MUST NOT be adjacent to dataviz-green (brand green) or status-active.
 */
export const scoreTokens = {
    "score-poor": { light: palette["red-45"], /* #B91C1C */ dark: palette["red-63"] /* #EF4444 */ },
    "score-fair": { light: palette["amber-46"], /* #854F0B — alias chart-3 */ dark: palette["amber-74"] /* #D4A356 */ },
    "score-good": { light: palette["blue-36"], /* #0C447C — alias dataviz-blue */ dark: palette["blue-62"] /* #5B8DC4 */ },
    // score-excellent = status-completed alias (v2 §G.3 obs 2 — both signal "celebrated end-state")
    "score-excellent": { light: palette["emerald-51"], /* #15803D */ dark: palette["emerald-79"] /* #4ADE80 */ },
} as const satisfies Record<string, AdaptiveColor>;

// ---------------------------------------------------------------------------
// ─── Bureau identity colors (coordinated) ───
// ---------------------------------------------------------------------------

/**
 * 3 bureau identity tokens — coordinated palette, NOT faithful brand colors (v2 §C.3 LOCKED).
 * Reasoning: faithful colors clash hard:
 *   - Experian green (#00A651) clashes with our brand primary
 *   - Equifax red (#C8102E) competes with destructive
 *   - TransUnion near-black reads as muted in dark mode
 * Used in score history chart legends + the dispute filings filter.
 */
// Coordination palette — design-coordinated colors (NOT faithful brand hex).
export const bureauTokens = { "bureau-equifax": { light: palette["pink-40"], /* #9D174D — magenta/pink */ dark: palette["pink-74"] /* #F472B6 */ }, "bureau-experian": { light: palette["orange-40"], /* #9A3412 — deep orange */ dark: palette["orange-74"] /* #FB923C */ }, "bureau-transunion": { light: palette["blue-32"], /* #334155 — slate */ dark: palette["blue-68"] /* #94A3B8 */ } } as const satisfies Record<string, AdaptiveColor>;

// ---------------------------------------------------------------------------
// ─── Dispute draft state colors (4 progression tiers) ───
// ---------------------------------------------------------------------------

/**
 * 7 dispute draft states → 4 progression tiers (v2 §C.4 + §G.3 obs 5); a canceled draft
 * takes none — the progression strip never draws it. Tier-keyed so a tier colour
 * changes once for every state that reads it.
 *
 * Used by the portal progression strip (PortalDisputeProgressionStrip) — a 4-tier step indicator
 * on portal dispute view per design-system.yml acceptance criterion.
 */
export const disputeStateTiers = { "tier-pending": { light: palette["stone-51"], /* #79706E — dataviz-neutral */ dark: palette["stone-69"] /* #A8A6A2 */ }, "tier-in-progress": { light: palette["blue-36"], /* #0C447C — dataviz-blue info */ dark: palette["blue-62"] /* #5B8DC4 */ }, "tier-ready": { light: palette["amber-46"], /* #854F0B — chart-3 warning */ dark: palette["amber-74"] /* #D4A356 */ }, "tier-complete": { light: palette["green-36"], /* #0F5D4C — primary */ dark: palette["green-66"] /* #1FA68C */ } } as const satisfies Record<string, AdaptiveColor>;

// ---------------------------------------------------------------------------
// ─── Action-risk colors (3 tiers) ───
// ---------------------------------------------------------------------------

/**
 * 5 action-risk values → 3 tiers (low/medium/high) per v2 §C.5 LOCKED.
 * Source: lib/actions/types.ts ActionRisk union (read | draft_write | tenant_write | external_effect | financial).
 *
 * Used in admin action-review UI (action-gate-layer) and risk-tier badges.
 */
export const actionRiskTiers = { "tier-low": { light: palette["slate-45"], /* #5F6470 — muted-fg */ dark: palette["slate-69"] /* #9BA1AB */ }, "tier-medium": { light: palette["amber-46"], /* #854F0B — chart-3 warning */ dark: palette["amber-74"] /* #D4A356 */ }, "tier-high": { light: palette["red-36"], /* #791F1F — destructive light */ dark: palette["red-62"] /* WCAG AA — matches --destructive dark */ } } as const satisfies Record<string, AdaptiveColor>;

// ---------------------------------------------------------------------------
// ─── Fluid AI gradient (mode-agnostic) ───
// ---------------------------------------------------------------------------

/**
 * Fluid gradient — the SECOND visual differentiator alongside brand primary (intake §2.1).
 * Mode-agnostic: light = dark = same hex per v2 §C.6 LOCKED + §G.3 obs 6.
 *
 * The ONLY token in the system with the mode-agnostic property — implementer must NOT
 * auto-pair into the {light, dark} structure assumed elsewhere.
 *
 * Used by:
 *   - FluidSession header rail (the only product surface where infinite animation is permitted)
 *   - FluidWorkspacePage AI surface accents
 *   - ChatKitAssistantPanel typing indicators
 *   - Fluid Auto Prepare progress UI
 *
 * Adjacency: Fluid endpoint #7DCFE0 cyan vs dataviz-blue dark #5B8DC4 — both pale blues in dark mode
 * but won't appear in same surface (Fluid lives in dedicated panels) — flag if a chart inside
 * a Fluid surface ever needs dataviz-blue (visual collision possible per v2 §G.3 obs 4).
 */
export const fluidGradient = {
    stops: [{ color: palette["green-36"], /* #0F5D4C — phthalo (matches brand primary) */ position: "0%" }, { color: palette["teal-58"], /* #1D9E89 — mid-teal */ position: "50%" }, { color: palette["cyan-81"], /* #7DCFE0 — cyan ('Fluid blue' from intake §2.1) */ position: "100%" }],
    shimmer: { duration_ms: 2400, easing: "var(--ease-pulse)", iteration: "infinite" }, // the curve lives once, as --ease-pulse
    // mode-agnostic — same hex applies to both light and dark
    text: { value: palette["teal-58"] /* #1D9E89 — mid-teal */ },
    focusRing: { value: palette["cyan-81"] /* #7DCFE0 — cyan endpoint */ },
} as const;

// ---------------------------------------------------------------------------
// ─── Semantic feedback (success / warning / info — aliases) ───
// ---------------------------------------------------------------------------

/**
 * 3 semantic feedback tokens — all aliases per v2 §C.7 LOCKED.
 *   success = brand primary (operator products convention — Linear, Stripe both use brand=success)
 *   warning = chart-3 amber
 *   info    = dataviz-blue
 *
 * Note (§2.7 + v2 §G.3 obs 1): status-active = dataviz-green = success = brand primary = 4 distinct
 * meanings sharing one color. Internally consistent (brand green = "good/normal/working").
 * <StatusBadge status="active"> and <Toast variant="success"> producing identical green chips
 * on the same screen is intentional, not a bug.
 */
export const semanticTokens = { success: { light: palette["green-36"], /* #0F5D4C — alias primary */ dark: palette["green-66"] /* #1FA68C */ }, warning: { light: palette["amber-46"], /* #854F0B — alias chart-3 */ dark: palette["amber-74"] /* #D4A356 */ }, info: { light: palette["blue-36"], /* #0C447C — alias dataviz-blue */ dark: palette["blue-62"] /* #5B8DC4 */ } } as const satisfies Record<string, AdaptiveColor>;

// ---------------------------------------------------------------------------
// ─── Logo geometry constants ───
// ---------------------------------------------------------------------------

/**
 * Logo-derived constants from the 4 SVGs (intake §A.1 logo geometry).
 * `logo-text-light` (#161817) is ALSO `sidebar` dark (intake §2.6) — confirmed deliberate
 * alignment; one source of truth.
 */
export const logoTokens = {
    "bar-graphite": palette["slate-32"], /* #3A3D40 — mark.svg rect 1 */
} as const;


// ---------------------------------------------------------------------------
// ─── Typography ───
// ---------------------------------------------------------------------------

/**
 * Typography — OpenAI Sans for display/body, Geist Mono for mono.
 * Px-aligned scale fixes html=16px and body=14px (intake §3.3).
 */
/**
 * The registry (src/design-system/registry.css) — the vocabulary a surface may
 * speak. registry.css is hand-written; these lists mirror its names so the
 * generated ESLint/Tailwind artifacts and the token types describe it.
 */
export const registryTextStyles = ["overline", "caption", "badge", "body", "label", "strong", "lead", "title", "heading", "display", "kpi", "score", "score-xl", "code", "marketing-title", "marketing-display", "hero", "hero-lg", "dropcap"] as const;
export const registryRadii = { chip: "var(--radius-chip)", control: "var(--radius-control)", field: "var(--radius-field)", panel: "var(--radius-panel)", card: "var(--radius-card)", full: "var(--radius-full)" } as const;

export const typography = {
    fonts: { display: { family: "OpenAI Sans", variable: "--font-display", fallback: "Inter, ui-sans-serif, system-ui, sans-serif", weights: [400, 500, 600, 700], personality: "geometric" }, body: { family: "OpenAI Sans", variable: "--font-body", fallback: "Inter, ui-sans-serif, system-ui, sans-serif", weights: [400, 500, 600] }, mono: { family: "Geist Mono", variable: "--font-mono", fallback: "ui-monospace, 'SF Mono', Menlo, monospace", weights: [400, 500, 700] } },
} as const;

// ---------------------------------------------------------------------------
// ─── Typography utility tokens ───
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// ─── Marketing-surface tokens (closure-cycle Decisions 12 + 13) ───
// ---------------------------------------------------------------------------

/**
 * Marketing-only design tokens. Source of the 4 standalone CSS vars emitted by
 * generate.ts as hand-written emit-lines (NOT via the typography-role or
 * AdaptiveColor paths) so they:
 *   1. emit EXACTLY --font-size-display-1/2 + --marketing-brand-primary/accent
 *      with ZERO extra role-derived vars (--font-weight/--leading/--tracking-*), and
 *   2. stay OUT of VALID_COLOR_TOKENS / tailwindColors (honors Decision 13's
 *      marketing-only scoping — other surfaces must use --primary/--accent,
 *      enforced via an eslint.config.mjs path-glob).
 *
 * Mode-agnostic (single value, :root only) — like the --fluid-* accessory
 * tokens. Display fonts intentionally EXCEED the 4xl marketing-hero "max"
 * (2.625rem), which was a CRM-content cap (closure-cycle Decision 12).
 */
export const marketingTokens = {
    /** Brand palette (Decision 13) — distinct bespoke marketing identity, OFF the core --primary teal/green. */
    brand: {
        primary: palette["orange-68"], // #f97316 orange
        accent: palette["slate-8"], // #0f172a navy
    },
} as const;

// ---------------------------------------------------------------------------
// ─── Spacing scale ───
// ---------------------------------------------------------------------------

/**
 * Spacing scale. Base = 0.25rem (4px), Tailwind default.
 * Touch-target minimum (44px) reflected in `touch` size — design-system.yml acceptance.
 */
export const spacing = {
    base: "0.25rem", // 4px
    scale: {
        "neg-1": "-1px", // negative margin collapse (sr-only pattern); the one negative rung — other negatives are calc(-1 * var(--space-N))
        "0": "0",
        "px": "1px",
        "0.5": "0.125rem", // 2px
        "1": "0.25rem", // 4px
        "1.5": "0.375rem", // 6px
        "2": "0.5rem", // 8px
        "2.5": "0.625rem", // 10px
        "3": "0.75rem", // 12px
        "3.5": "0.875rem", // 14px — gap between 3/4; 9 files use 0.875rem in icon/spacing contexts; normalization-v2 A-TOKEN-ADD-misc
        "4": "1rem", // 16px
        "5": "1.25rem", // 20px
        "6": "1.5rem", // 24px
        "7": "1.75rem", // 28px
        "8": "2rem", // 32px
        "9": "2.25rem", // 36px
        "10": "2.5rem", // 40px
        "11": "2.75rem", // 44px — minimum touch target (WCAG 2.5.5 + design-system.yml)
        "12": "3rem", // 48px
        "14": "3.5rem", // 56px — nav bar height, sticky header slots
        "16": "4rem", // 64px
        "18": "4.5rem", // 72px — leftover page insets; billing already referenced --space-18
        "20": "5rem", // 80px
        "24": "6rem", // 96px
        "28": "7rem", // 112px — skeleton bars, signature-pad min-height
        "32": "8rem", // 128px — skeleton cards, image placeholders
        "36": "9rem", // 144px — recurring geometry value (3+ files); Step-B addition
        "40": "10rem", // 160px — wider skeleton bars, search widths
        "48": "12rem", // 192px — skeleton section widths, avatar placeholders
        "56": "14rem", // 224px — skeleton bars, sidebar/nav widths (6 files); normalization-v2 A-TOKEN-ADD-misc
        "64": "16rem", // 256px — skeleton/layout widths (4+ files); normalization-v2 A-TOKEN-ADD-misc
        "72": "18rem", // 288px — batch mint: padding-block 18rem literal (3 sites)
        "80": "20rem", // 320px — sidebar widths, long skeleton bars
        "100": "25rem", // 400px — max-height scroll containers
    },
    /** Parent-relative spacing, emitted as --space-em-{key}: a quarter-em ladder for prose rhythm that
     *  scales with its own font size (heading and list spacing, inline code padding). A value off the
     *  ladder snaps to the nearest rung, ties lower — D121's rule for the rem scale (folded 2026-10-13). */
    em: {
        "1": "1em",
        "25": "0.25em",
        "5": "0.5em",
        "75": "0.75em",
        "125": "1.25em",
        "150": "1.5em",
        "175": "1.75em",
    },
} as const;

// ---------------------------------------------------------------------------
// ─── Border radius scale ───
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// ─── Border width scale ───
// ---------------------------------------------------------------------------

/**
 * Border widths (intake §2.4 — 2px RESERVED for focus rings).
 * `2px` is RESERVED for focus rings only per intake §2.4 (2px solid + 2px outline-offset).
 */
/* Every width is a spacing rung read by role: the spacing scale is the one
   primitive length ladder, so a border width never restates a pixel value. */
export const borderWidth = {
    DEFAULT: "var(--space-px)", // 1px hairline
    "2": "var(--space-0_5)", // 2px — RESERVED — focus rings (intake §2.4); design-system.yml mandates ring 2px solid
    "4": "var(--space-1)", // 4px — accent stripes (e.g. blockquote left-border)
} as const;

// ---------------------------------------------------------------------------
// ─── Shadow tokens (multi-layer, neutral-tinted) ───
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// ─── Focus / ring shadow tokens (B2 — operator-approved Group B mint) ───
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// ─── Blur tokens ───
// ---------------------------------------------------------------------------

/**
 * Blur values for backdrop-filter and filter properties.
 * `base` (8px) = Tailwind `backdrop-blur` default — the standard frosted-glass
 * overlay depth used by StickyActionBar, FloatingDock, AdminNavigation, etc.
 */
/* Blur radii read the spacing scale by role — one length ladder, no restated pixels. */
export const blur = {
    xs: "var(--space-0_5)", // 2px — backdrop-filter: blur(2px) (molecules/data-display.css)
    sm: "var(--space-1)", // 4px — sub-base blur
    base: "var(--space-2)", // 8px — Tailwind backdrop-blur default; all `backdrop-filter: blur(8px)` usages
    md: "var(--space-3)", // 12px — filter/backdrop-filter Phase 6 sweep
} as const;

// ---------------------------------------------------------------------------
// ─── Motion (durations + easings + presets) ───
// ---------------------------------------------------------------------------

/**
 * Motion (intake §4.3 — "productive": fast 100ms / base 150ms / slow 200ms / page 200ms).
 * Reduce-motion: STRICT (intake §4.3) — clamps all durations to 0ms when prefers-reduced-motion: reduce.
 */
export const transitions = {
    durations: {
        fast: "100ms", // button press, intake §4.3 fast
        base: "150ms", // default transitions, intake §4.3 base
        slow: "200ms", // dialog/sheet, intake §4.3 slow + page (same value per intake §4.3)
        moderate: "300ms", // the sheet/dialog open (motionPresets.dialogOpen, sheetSlide, toastSlide — HONOR TOKENS.TS 300ms); the marketing word-stagger cap
        // ── Continuous-loop scale (extends beyond intake §4.3 "snappy under 200ms"
        //    event-motion personality, which governs discrete enter/exit transitions
        //    and not infinite loops). Precedents: motionPresets.skeletonShimmer
        //    (2000ms) and fluidGradient.shimmer.duration_ms (2400ms, v2 §C.6 LOCKED)
        //    establish per-role infinite-loop tokens; this scale generalizes the
        //    common cycle durations across loading-indicator atoms (Spinner ring
        //    rotation, Loader dot bounce / pulse / ping / loading-dots / shimmer-text).
        "loop": "1000ms", // typical loading-indicator cycle (spin / bounce / pulse / ping)
        "loop-slow": "2000ms", // contemplative shimmer-style effects (Loader text-shimmer); "slowest" closes the scale (no implied tier above)
        // ── Closure-cycle gap tokens (Decision 16) — no existing duration matched these stamp sites.
        //    NOTE: --duration-micro (16ms) was DEFERRED to operator — name collides with the
        //    existing micro=80ms; do NOT add it here.
        // ── TOKEN-CENTRALIZATION-CLOSURE Decision 6 / Decision 3 motion durations ──
        // These close the 9 catalog GAP rows in motion duration (shimmer-text, beam, marquee).
        // Names follow Phase 5 adjudication (fix-lead ruling supersedes scope-reconciliation doc names).
        // B4 — animation-delay stagger tokens (operator-approved Group B, 2026-05-30)
        // Positive delays: map.css cascade stagger (map pin sequential reveal)
        // Negative delays: loading.css reverse-stagger trick (start mid-cycle for continuous feel)
        // Batch 4 mint — literal animation durations (s-unit format for CSS literal match)
        // NOTE: "marquee": "20s" SKIPPED — key collision with existing "marquee": "20000ms"
    },
    easings: {
        enter: "cubic-bezier(0.16, 1, 0.3, 1)", // overshoot entrance
        exit: "cubic-bezier(0.4, 0, 1, 1)", // ease-in — exits
        standard: "cubic-bezier(0.4, 0, 0.2, 1)", // Material/CSS standard ease-in-out (closure-cycle Decision 10a); 10+ files
        linear: "linear", // B5 — animation spin/loop easing (~21+ dw-spin sites; operator-approved 2026-05-30)
        "step-end": "step-end", // a caret blinks, it does not fade
        pulse: "cubic-bezier(0.4, 0, 0.6, 1)", // the pulse/shimmer curve (Tailwind's ease-in-out for pulse); the Fluid shimmer and the skeleton pulse both read it
    },
    signature: "cubic-bezier(0.25, 0.1, 0.25, 1.0)", /* intake §4.3 LOCKED */
    reduceMotion: "strict" as const, /* intake §4.3 — clamp durations to 0ms when prefers-reduced-motion: reduce */
} as const;

/**
 * Per-role motion presets (intake §4.1-§4.3 + intake-answers-v3 §N choreography).
 * `skeletonShimmer` is a shared loading animation for SkeletonPage molecules (design-system.yml).
 *
 * ⚠️ DIVERGENCE-3/4/5 RATIFIED (design-system-completion-20260527 Decision 7):
 *   Intake §N.1 LOCKED: modal_enter=200ms+productive_easing, sheet_enter=200ms, toast_enter=150ms.
 *   tokens.ts: dialogOpen/sheetSlide/toastSlide all use 300ms+overshoot(cubic-bezier(0.16,1,0.3,1)).
 *   Rationale: more expressive opening feel. Operator decision 2026-05-27: HONOR TOKENS.TS.
 */
export const motionPresets = {
    // Interactive feedback
    buttonHover: { duration: "80ms", easing: "cubic-bezier(0.4, 0.36, 0, 1)" },
    buttonPress: { duration: "100ms", easing: "cubic-bezier(0.4, 0, 1, 1)" },
    inputFocus: { duration: "80ms", easing: "cubic-bezier(0.4, 0.36, 0, 1)" },
    rowHover: { duration: "80ms", easing: "cubic-bezier(0.4, 0.36, 0, 1)" },

    // Layout transitions (DIVERGENCE-3/4: spec=200ms; HONOR TOKENS.TS: 300ms+overshoot)
    dialogOpen: { duration: "300ms", easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
    dialogClose: { duration: "150ms", easing: "cubic-bezier(0.4, 0, 1, 1)" },
    sheetSlide: { duration: "300ms", easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
    popoverOpen: { duration: "100ms", easing: "cubic-bezier(0.4, 0.36, 0, 1)" },
    tooltipOpen: { duration: "100ms", easing: "cubic-bezier(0.4, 0.36, 0, 1)" },

    // Page-level
    pageEnter: { duration: "400ms", easing: "cubic-bezier(0.2, 0, 0, 1)" },
    routeTransition: { duration: "150ms", easing: "cubic-bezier(0.4, 0.36, 0, 1)" },

    // Status feedback
    successCheckmark: { duration: "600ms", easing: "cubic-bezier(0.34, 1.3, 0.64, 1)" },
    errorShake: { duration: "400ms", easing: "cubic-bezier(0.34, 1.3, 0.64, 1)" },
    toastSlide: { duration: "300ms", easing: "cubic-bezier(0.16, 1, 0.3, 1)" }, // DIVERGENCE-5: spec=150ms; HONOR TOKENS.TS

    // Data
    chartMorph: { duration: "400ms", easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
    numberCount: { duration: "600ms", easing: "cubic-bezier(0.22, 1, 0.36, 1)" },

    // Loading — design-system.yml SkeletonPage acceptance
    skeletonShimmer: { duration: "2000ms", easing: "cubic-bezier(0.4, 0, 0.6, 1)", iteration: "infinite" }, // matches production skeleton.css var(--duration-loop-slowest); design-review candidate 2026-05-27

    // Loading-indicator atoms (Spinner + Loader variants). Per-role
    // documentation parallel to skeletonShimmer + fluidGradient.shimmer; each
    // entry pins a specific role to one duration + easing. Atom CSS consumes
    // the underlying var(--duration-loop-*) tokens directly — these constants
    // are the spec authority that the CSS comment-citations point back to.
    spinnerSpin: { duration: "1000ms", easing: "linear", iteration: "infinite" },
    loaderBounce: { duration: "1000ms", easing: "ease-in-out", iteration: "infinite" },
    loaderPulse: { duration: "1000ms", easing: "ease-in-out", iteration: "infinite" },
    loaderPing: { duration: "1000ms", easing: "ease-out", iteration: "infinite" },
    loaderShimmerText: { duration: "2000ms", easing: "linear", iteration: "infinite" },
    loaderLoadingDots: { duration: "1400ms", easing: "ease-in-out", iteration: "infinite" },
} as const;

// ---------------------------------------------------------------------------
// ─── Z-index scale ───
// ---------------------------------------------------------------------------

export const zIndex = {
    behind: "-1", // a probe or backdrop layered beneath its own stacking context (auto-fit probes, the dev dashboard's dark veil)
    base: "0",
    "above-base": "1", // TOKEN-CENTRALIZATION-CLOSURE Decision 6 — z-index:1 gap (87 CSS uses)
    // Local stacking — order INSIDE one component's own stacking context, where
    // the app-wide layer ladder below does not apply: a grip over its panel, a
    // handle over its node, a menu over its row. Named by rung, like a spacing
    // step, because the number IS the order; siblings read neighbouring rungs.
    "local-2": "2",
    "local-3": "3",
    "local-4": "4",
    "local-6": "6",
    "local-8": "8",
    "raised-sm": "5", // TOKEN-CENTRALIZATION-CLOSURE Decision 6 — z-index:5 gap (small raised elements)
    raised: "10",
    dropdown: "20",
    sticky: "30",
    overlay: "40",
    modal: "50",
    banner: "60",
    priority: "70", // TOKEN-CENTRALIZATION-CLOSURE Decision 6 — z-index:70 gap (between banner + max)
    max: "100",
    "above-all": "9999", // TOKEN-CENTRALIZATION-CLOSURE Decision 6 — topmost sentinel (pre-declared)
} as const;

// ---------------------------------------------------------------------------
// ─── Opacity scale ───
// ---------------------------------------------------------------------------

/**
 * Opacity scale.
 * `medium` (0.20) is the design-system.yml mandate for disabled states (background opacity).
 */
export const opacity = {
    faint: "0.05", // drop-zone active background tint (intake v3 §K.2 LOCKED — Decision 7 MISSING-2)
    light: "0.10",
    soft: "0.15", // the badge tone ladder's soft background — the rung between light and medium
    medium: "0.20", // design-system.yml: disabled-state background opacity
    low: "0.30", // TOKEN-CENTRALIZATION-CLOSURE Decision 6 — opacity:0.30 scale extension
    mid: "0.40", // the tone ring's default strength (registry.css --ring-tone: primary at 40%)
    half: "0.50", // drag ghost element opacity (intake v3 §K.2 LOCKED — Decision 7 MISSING-3)
    high: "0.70", // TOKEN-CENTRALIZATION-CLOSURE Decision 6 — opacity:0.70 scale extension
    almost: "0.90", // TOKEN-CENTRALIZATION-CLOSURE Decision 6 — opacity:0.90 scale extension
} as const;

// ---------------------------------------------------------------------------
// ─── Layout dimensions ───
// ---------------------------------------------------------------------------


/**
 * Responsive breakpoints (design-system.yml CC-1 mobile contract).
 * Arbitrary breakpoints in cva or Tailwind classes are forbidden via ESLint rule
 * the breakpoint-enforcement rule planned in Step 7 (never implemented).
 *
 * Per-surface mobile contract (design-system.yml acceptance):
 *   - CRM:       sidebar collapses at <md (768px); page-header stacks above content at <sm (640px)
 *   - Portal:    mobile-first (single-column at <md, never side-rail)
 *   - Marketing: fluid-responsive end-to-end
 */
export const breakpoints = {
    xs: "320px", // smallest supported phone width (design-system.yml)
    sm: "640px", // small tablet / large phone
    md: "768px", // tablet / sidebar-collapse threshold for CRM
    lg: "1024px", // small laptop
    xl: "1280px", // desktop
    "2xl": "1536px", // wide desktop
} as const;

/**
 * Layout dimensions (intake §4.4 density preference).
 * `density: comfortable` (intake §4.4) → row 44, button 36, input 36.
 */
export const layout = {
    // Navigation (intake §4.4). A layout size that coincides with a spacing
    // rung reads the rung: the value is defined once and the role stays a knob.
    "navbar-h": "var(--space-16)", // 64px
    // The control panel (CRM sidebar). The expanded panel is position-fixed with an
    // in-flow gap reserving its width, so the width is a declared size, not a
    // content measure; the sidebar component reads these three as its defaults.
    "sidebar": "var(--space-40)", // 160px — the smallest spacing rung above what the longest label needs: 8 group + 8 button + 16 icon + 8 gap + 90 ("Dispute Engine") + 8 + 8 = 146px
    "sidebar-mobile": "18rem", // 288px — the off-canvas sheet on phones

    // Far enough off-screen that no measurement probe is ever seen or scrolled
    // to. A sentinel, not a design step: the worklist's auto-fit probes render
    // real rows at max-content width out here to measure them.
    "offscreen": "-100000px",

    // Content frame
    "content-max-w": "80rem", // 1280px
    "content-max-w-lg": "102.5rem", // 1640px — the portal dashboard, which carries a rail beside its content
    "content-px": "var(--space-6)", // 24px
    "content-py": "var(--space-4)", // 16px — the CRM page's top and bottom inset (2026-10-26: 24px read as a gap under the navbar)
    "section-gap": "var(--space-6)", // 24px

    // Content well max-widths (Step B token-gap candidates; 3+ MISSING-TOKEN hits each)
    // These are semantic max-width constraints for content wells, error states, and
    // modals that need a narrower cap than --layout-content-max-w.
    // Note: --layout-dialog-w (32rem) and --layout-dialog-w-xl (48rem) already cover
    // those widths for dialog chrome; these tokens are for non-dialog content containers.
    //         Note: --layout-dialog-w is also 32rem but semantically for dialog chrome.
    //         Note: --layout-dialog-w-xl is also 48rem but semantically for dialog xl chrome.

    // Grid

    // Tables (density-driven; intake §4.4 = comfortable)

    // Density (intake §4.4 LOCKED)

    // Dialog widths
    "dialog-w": "32rem",

    // Sheet (side panel) widths
    "sheet-w-lg": "30rem",

    // Misc

    // Frame previews (used in editor previews)
    "frame-mobile-aspect": "433 / 882",

    // Forced-context preview backgrounds (not theme-dependent)
    "preview-light-bg": palette["neutral-99"], /* #FBFBFC */
    "preview-dark-bg": palette["neutral-20"], /* #1B1D20 */

    /* The layout family (ruled 2026-09-16): the sizes a SURFACE takes, where
       neither a spacing rung nor a measure answers. Twelve names at values
       already in the tree; a call site converts when it lands within a tenth
       of a rung, so near neighbours fold and nothing far moves. Skeleton
       geometry is out of scope — it traces the card it stands in for. */
    "rail-sm": "11rem", /* 176px — a help sidebar, a narrow nav */
    "pane": "22rem", /* 352px — a list pane beside a detail view */
    "stage-sm": "11.25rem", /* 180px — a meter, a short chart */
    "stage": "18.75rem", /* 300px — the standard drawing stage */
    "stage-lg": "31.25rem", /* 500px — a map canvas */
    "scroll-sm": "12.5rem", /* 200px — a short scroll body */
    "scroll": "22.5rem", /* 360px — a scroll body */
    "scroll-lg": "40rem", /* 640px — a tall scroll body */
    "overlay-sm": "21.875rem", /* 350px — a popover, a picker */
    "overlay": "var(--measure-sm)", /* 448px — a dialog: the same width the measure ladder names for one. Wider panels read --measure-*. */
} as const;

// ---------------------------------------------------------------------------
// ─── Iconography (Heroicons) ───
// ---------------------------------------------------------------------------

/**
 * Iconography — intake §4.5 LOCKED specifies Heroicons over lucide.
 *
 * Rationale (intake §4.5 walkthrough log, 2026-05-03):
 *   "icons — Heroicons (outline 24/solid 20/micro 16), 20px default, 1.5
 *    stroke, rounded — overrode Lucide proposal; Lucide geometry too
 *    'friendly startup' (shield/wallet specifically); Heroicons matches
 *    Stripe Dashboard register and Tailwind ecosystem."
 *
 * Three sets: 24px outline (default UI), 20px solid (active/filled
 * states + badges), 16px solid (tight inline / table cells). Active vs
 * inactive nav state uses outline → solid swap (intake §4.5 note).
 *
 * MIGRATION STATE (transitional — pre-Phase-1-Step-14):
 *   The codebase widely uses `lucide-react` (predates the intake §4.5
 *   Heroicons LOCK). Phase 1 Step 14 swaps the imports:
 *   `import { Foo } from "lucide-react"` →
 *   `import { Foo } from "@heroicons/react/24/outline"` (or `/solid` for
 *   active states). The `@heroicons/react` package is not installed yet;
 *   Step 14 owns installing it + running the migration codemod.
 *   Reference impls in docs/reference/ may still import lucide during
 *   transition; that's expected, not a divergence.
 */
export const icons = {
    library: "heroicons" as const,
    defaultSizePx: 20,
    defaultStrokePx: 1.5,
} as const;

// ---------------------------------------------------------------------------
// ─── Imagery (intake §4.6) ───
// ---------------------------------------------------------------------------

/**
 * Imagery rules (intake §4.6 LOCKED).
 * `empty_state: icon-only` — design-system.yml: 32-48px icon, NO 64px-circle container
 * (intake §6.2 explicitly rejects the consumer-app pattern).
 */
export const imagery = {
    photography: "none" as const,
    illustration: "none" as const,
    emptyState: "icon-only" as const,
    emptyStateIconSizeRange: { min: 32, max: 48 } as const, // intake §6.2 LOCKED
} as const;

// ---------------------------------------------------------------------------
// ─── Border-style tokens (TOKEN-CENTRALIZATION-CLOSURE Decision 6) ───
// ---------------------------------------------------------------------------

/**
 * Border-style token scale. Phase 6 sweep routes all raw `border-style: solid`
 * etc. declarations to `border-style: var(--border-style-*)`.
 * `divider` is a semantic alias for the most common divider use case.
 * Emitted as --border-style-{key}.
 */
export const borderStyle = {
    solid: "solid",
    dashed: "dashed",
    dotted: "dotted", // the worklist's provisional-address underline
} as const;

// ---------------------------------------------------------------------------
// ─── Cursor tokens (TOKEN-CENTRALIZATION-CLOSURE Decision 6) ───
// ---------------------------------------------------------------------------

/**
 * Cursor token scale. Phase 6 sweep routes all raw `cursor: pointer` etc.
 * declarations to `cursor: var(--cursor-*)`. 195 CSS decls; top values:
 * pointer(101), not-allowed(37), text(18), default(15), help(6).
 * Emitted as --cursor-{key}.
 */
export const cursor = {
    interactive: "pointer", // primary interactive affordance (links, buttons, clickable cards)
    disabled: "not-allowed", // non-interactive / disabled state
    text: "text", // text input areas
    default: "default", // reset to system default arrow
    help: "help", // tooltip-bearing elements (?) cursor
    grab: "grab", // draggable elements (resting state)
    grabbing: "grabbing", // draggable elements (active drag)
    crosshair: "crosshair", // precision drawing / signature canvas (.dw-inline-agreement-signer__sig-canvas)
    // Batch mint [decision] hx7n872wcetad3d56mftvqb9rh87pvrc
    "resize-col": "col-resize", // horizontal resize handle (column splitters / rail dividers)
    "resize-row": "row-resize", // vertical resize handle (a split between stacked panes, a card's height)
    "resize-nwse": "nwse-resize", // CONF-MINT-GAP-BATCH: diagonal resize NW↔SE (3 sites)
    "resize-nesw": "nesw-resize", // CONF-MINT-GAP-BATCH: diagonal resize NE↔SW (2 sites)
    wait: "wait", // blocking async operation (spinner cursor)
    move: "move", // a draggable widget body (dashboard grid)
    "context-menu": "context-menu", // a right-click target (the agent builder canvas)
} as const;

// ---------------------------------------------------------------------------
// ─── Filter function tokens ───
// ---------------------------------------------------------------------------

/**
 * Filter amounts — the arguments a `filter:` function takes. Emitted as
 * --filter-{key}; a rule writes `filter: brightness(var(--filter-brightness-hover))`.
 * One hover lift and one saturation boost: the tree carried 1.05 / 1.08 / 1.1
 * for the same job (lift on hover) and they fold onto the atom tier's 1.1.
 */
export const filterAmount = {
    "brightness-hover": "1.1", // status badge, marketing CTA and Fluid submit lift on hover
    "brightness-down-sm": "0.98", // the half-step below — a resting tile against its neighbour
    "saturate-boost": "1.15", // shimmer and feature-art colour boost
    "saturate-mute": "0.92", // a canvas behind a modal loses a step of colour
    // Grayscale's steps read the fraction ladder — the same 0-1 rungs --opacity-*
    // owns — so one retune moves both. "full" is written as a percentage instead:
    // it is the end of the scale rather than a rung on it, and the number 1 is
    // already --z-above-base, which it has nothing to do with.
    "grayscale-full": "100%", // a retired or out-of-round tile reads as history
    "grayscale-strong": "var(--opacity-high)",
} as const;


// ---------------------------------------------------------------------------
// ─── Aspect-ratio tokens (TOKEN-CENTRALIZATION-CLOSURE Decision 6) ───
// ---------------------------------------------------------------------------

/**
 * Standalone aspect-ratio token scale. Supplements the layout.frame-*-aspect
 * tokens (which are for editor frame previews). These four are general-purpose
 * ratios used across cards, media embeds, and grid items.
 * Emitted as --aspect-{key}.
 */
export const aspectRatio = {
    square: "1 / 1", // square cards, avatar containers
    video: "16 / 9", // video embeds, preview cards
    card: "4 / 5", // portrait cards (taller-than-wide)
    widescreen: "16 / 10", // CONF-MINT-GAP-BATCH: 2 sites using 16:10 widescreen ratio
    // The marketing site's media shapes (home.css) — two design ratios and the
    // intrinsic dimensions of its screenshots and video, named by what they frame.
    photo: "3 / 2", // how-to art media
    portrait: "6 / 7", // the founders' photo pile
    "hero-shot": "3024 / 1642", // the hero screenshot
    "feature-card": "344 / 610", // a tall feature card
    "feature-card-wide": "1296 / 656", // a wide feature card
    "feature-media": "450 / 380", // feature card media
    "feature-mini": "564 / 340", // a mini feature tile
    "blog-card": "40 / 21", // a blog card's media
    "plates-video": "1920 / 1072", // the plates' video
    // The portal-editor emulator's two device stages.
    laptop: "1024 / 680", // the desktop shell and its zoom stage
    // Batch 5 supplement — ShowcaseLaptop display panel exact pixel ratio
} as const;

// ---------------------------------------------------------------------------
// ─── Border-overlay tokens (TOKEN-CENTRALIZATION-CLOSURE Decision 3) ───
// ---------------------------------------------------------------------------

/**
 * Border overlay pair — static (not adaptive). These are transparent-black and
 * transparent-white overlays used as border tints to add visual depth WITHOUT
 * requiring a separate border color token for every surface.
 * - `--border-overlay-dark`: subtle dark tint (8% black) used in light mode
 *   borders for inset/inward shadow effect. 30 CSS GAP rows.
 * - `--border-overlay-light`: subtle light tint (5% white) used in dark mode
 *   borders for glassy/frosted highlight effect.
 * Emitted as --border-overlay-{key} (NOT adaptive — both values always present).
 */
// ---------------------------------------------------------------------------
// ─── Overlay scrim scale (TOKEN-CENTRALIZATION-CLOSURE Decision 3) ───
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// ─── Bureau faithful brand colors (TOKEN-CENTRALIZATION-CLOSURE Decision 3) ───
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// ─── Component semantic aliases — Phase 9A ([decision] hx7wfwfhwkxqpphq3gm9p4pg6187pbqx) ───
// ---------------------------------------------------------------------------

/**
 * Component-intent aliases over primitive scale tokens.
 * Each alias anchors one role-class to a single primitive; a theme edit
 * to --card-radius re-skins every card in the product.
 *
 * Emitted as --{key} (no prefix) into :root by generate.ts.
 * Spec sources: intake §2.2, §4.1, §4.4 (v2); §R.1, §B.2 (v3).
 * Authorized: [decision] hx7wfwfhwkxqpphq3gm9p4pg6187pbqx (2026-05-30).
 *
 * DO NOT add without a new coordinator [decision] event (Wall #5).
 * DO NOT rename — HR16.
 */
export const componentAliases: Record<string, string> = {
    // Radius — intake §4.1
    // "card-radius" retired 2026-09-15: it had no reader, and the card registry
    // (card-registry.ts) now emits --card-radius as the role every card reads.

    // Component spacing — intake §4.4 comfortable density + v3 §R.1
    // "card-padding" retired 2026-09-15: no reader; the card registry's --card-inset is the role.

    // Density matrix — v3 §B.2 wizard.step_padding per surface

    // Density matrix — v3 §B.2 dashboard.widget_grid_gap per surface

    // Modal surface roles — intake §2.2 Level 3 (popover surface) EXPLICIT

    // Keyframe opacity — browser-floor authorized ([decision] hx7hxjym0xh8er27cfgk4y8rp587qdfz)
    // Value is NUMBER form (0.5, not 50%) — required for keyframe-body compatibility.
    // Usage: var(--opacity-pulse-mid) — bare var(); fallback stripped per director ruling (gate detects fallback literals).
    "opacity-pulse-mid": "var(--opacity-half)", // nadir opacity for pulse-cycle animations — the half step, read by role
    // Batch 3 mint
    // Batch 6 mint
    // Batch 5 — padding/sizing component tokens
    // Batch 5 supplement — font family
};

// ---------------------------------------------------------------------------
// ─── Color-mix amount scale ───
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// ─── DEPRECATED legacy fixedTokens aggregate ───
// ---------------------------------------------------------------------------

/**
 * DEPRECATED — kept for back-compat with 1 existing consumer (dev workbench).
 * Aggregates the new typed token categories into the legacy `fixedTokens` shape.
 * Removal target: Phase 1 close (Step 28). New code should import the typed
 * categories directly (statusTokens, scoreTokens, bureauTokens, etc.).
 *
 * `dataviz-3` and `dataviz-6` are remaining deprecated aliases.
 * Source: existing codebase usage; not in the new locked palette (intake §2.8).
 * Note: dataviz-1/2/4/5 have been removed — use dataviz-green/blue/purple/neutral directly.
 */
const legacyDatavizTokens = { "dataviz-3": { light: palette["amber-46"], /* #854F0B — alias chart-3 */ dark: palette["amber-74"] /* #D4A356 */ }, "dataviz-6": { light: palette["red-45"], /* #B91C1C — alias score-poor */ dark: palette["red-63"] /* #EF4444 */ } } as const satisfies Record<string, AdaptiveColor>;

/** @deprecated Use the typed category exports (statusTokens, scoreTokens, etc.) directly. */
export const fixedTokens = { ...semanticTokens, ...scoreTokens, ...statusTokens, ...bureauTokens, ...legacyDatavizTokens } as const;

// ---------------------------------------------------------------------------
// ─── Type exports (DO NOT manually maintain — generate.ts validates) ───
// ---------------------------------------------------------------------------

// Color tokens — flattened union across every category
export type ThemeableTokenName = keyof typeof themeableTokens;
export type StatusTokenName = keyof typeof statusTokens;
export type ScoreTokenName = keyof typeof scoreTokens;
export type BureauTokenName = keyof typeof bureauTokens;
export type DisputeStateTier = keyof typeof disputeStateTiers;
export type ActionRiskTier = keyof typeof actionRiskTiers;
export type ActionRiskTokenName = ActionRisk;
export type DisputeState = DraftStatus;
export type SemanticTokenName = keyof typeof semanticTokens;

export type ColorTokenName = ThemeableTokenName | StatusTokenName | ScoreTokenName | BureauTokenName | DisputeStateTier | SemanticTokenName;

// Status / score / bureau / dispute / risk — for component prop types
export type ClientStatus = StatusTokenName;

// Typography
export type TypographyRole = (typeof registryTextStyles)[number];
export type FontFamilyName = keyof typeof typography.fonts;

// Shadow

// Motion
export type MotionDurationName = keyof typeof transitions.durations;
export type MotionEasingName = keyof typeof transitions.easings;
export type MotionPresetName = keyof typeof motionPresets;

// Z-index
export type ZIndexName = keyof typeof zIndex;

// Opacity
export type OpacityName = keyof typeof opacity;

// Layout
export type LayoutToken = keyof typeof layout;
export type BreakpointName = keyof typeof breakpoints;

// Radius
export type RadiusName = keyof typeof registryRadii;

// Border width
export type BorderWidthName = keyof typeof borderWidth;

// Spacing
export type SpacingScaleName = keyof typeof spacing.scale;

// Icons
/** The four icon sizes, smallest first — the list the generated
 *  VALID_ICON_SIZES is written from. */
export const ICON_SIZE_NAMES = ["sm", "default", "lg", "xl"] as const;

export type IconSizeName = (typeof ICON_SIZE_NAMES)[number];

// ---------------------------------------------------------------------------
// ─── Helpers (getAllColorTokens, getTypographyRoleNames, etc.) ───
// ---------------------------------------------------------------------------
/**
 * The shape that org presets must conform to — only themeable tokens.
 * Source: blueprint §3.5.
 */
export type ThemeableTokenKey = keyof typeof themeableTokens;








/**
 * Surface elevation utility.
 * Maps elevation level (0-3) to themeable token name. Used by Card / Popover / Sheet to pick
 * its surface from a known elevation level — eliminates ad-hoc bg-color choices.
 */
export function surface(level: 0 | 1 | 2 | 3): ThemeableTokenName {
    return (["background", "muted", "card", "popover"] as const)[level];
}

